import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  MapPin, 
  Phone, 
  Users, 
  Home, 
  Heart, 
  Utensils, 
  Package, 
  Truck,
  Search,
  CheckCircle,
  AlertTriangle,
  Loader
} from 'lucide-react';
import toast from 'react-hot-toast';
import { CreateResourceData } from '../../types/resource';
import { ResourceService } from '../../services/resourceService';
import { useLocationDetection } from '../../hooks/useLocationDetection';

interface AddResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResourceAdded: () => void;
}

const AddResourceModal: React.FC<AddResourceModalProps> = ({ isOpen, onClose, onResourceAdded }) => {
  const { location: userLocation } = useLocationDetection();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [formData, setFormData] = useState<CreateResourceData>({
    name: '',
    type: 'shelter',
    location_name: '',
    address: '',
    city: userLocation?.city || '',
    region: userLocation?.region || '',
    country_code: userLocation?.countryCode || 'US',
    latitude: userLocation?.coordinates?.lat || 0,
    longitude: userLocation?.coordinates?.lng || 0,
    contact: '',
    capacity: 0,
    available: 0,
    status: 'open',
    description: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Update form data when user location changes
  useEffect(() => {
    if (userLocation && !formData.city) {
      setFormData(prev => ({
        ...prev,
        city: userLocation.city || '',
        region: userLocation.region || '',
        country_code: userLocation.countryCode || 'US',
        latitude: userLocation.coordinates?.lat || 0,
        longitude: userLocation.coordinates?.lng || 0
      }));
    }
  }, [userLocation]);

  const resourceTypes = [
    { value: 'shelter', label: 'Emergency Shelter', icon: Home, description: 'Temporary housing and accommodation' },
    { value: 'medical', label: 'Medical Facility', icon: Heart, description: 'Healthcare services and medical aid' },
    { value: 'food', label: 'Food Distribution', icon: Utensils, description: 'Food and water distribution center' },
    { value: 'supply', label: 'Supply Center', icon: Package, description: 'Emergency supplies and equipment' },
    { value: 'transport', label: 'Transportation', icon: Truck, description: 'Evacuation and transport services' }
  ];

  const statusOptions = [
    { value: 'open', label: 'Open', color: 'text-accent-green' },
    { value: 'full', label: 'At Capacity', color: 'text-accent-orange' },
    { value: 'closed', label: 'Closed', color: 'text-accent-red' }
  ];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Resource name is required';
    if (!formData.location_name.trim()) newErrors.location_name = 'Location name is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.contact.trim()) newErrors.contact = 'Contact information is required';
    if (formData.capacity <= 0) newErrors.capacity = 'Capacity must be greater than 0';
    if (formData.available < 0) newErrors.available = 'Available spaces cannot be negative';
    if (formData.available > formData.capacity) newErrors.available = 'Available spaces cannot exceed capacity';
    if (!formData.description.trim()) newErrors.description = 'Description is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGeocodeAddress = async () => {
    if (!formData.address.trim()) {
      toast.error('Please enter an address first');
      return;
    }

    setIsGeocoding(true);
    try {
      const fullAddress = `${formData.address}, ${formData.city}, ${formData.region}, ${formData.country_code}`;
      const coordinates = await ResourceService.geocodeAddress(fullAddress);
      
      if (coordinates) {
        setFormData(prev => ({
          ...prev,
          latitude: coordinates.lat,
          longitude: coordinates.lng
        }));
        toast.success('Address geocoded successfully');
      } else {
        toast.error('Could not find coordinates for this address');
      }
    } catch (error) {
      toast.error('Failed to geocode address');
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);
    try {
      await ResourceService.addResource(formData);
      toast.success('Emergency resource added successfully!');
      onResourceAdded();
      onClose();
      
      // Reset form
      setFormData({
        name: '',
        type: 'shelter',
        location_name: '',
        address: '',
        city: userLocation?.city || '',
        region: userLocation?.region || '',
        country_code: userLocation?.countryCode || 'US',
        latitude: userLocation?.coordinates?.lat || 0,
        longitude: userLocation?.coordinates?.lng || 0,
        contact: '',
        capacity: 0,
        available: 0,
        status: 'open',
        description: ''
      });
      setErrors({});
    } catch (error: any) {
      console.error('Error adding resource:', error);
      toast.error(error.message || 'Failed to add resource. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof CreateResourceData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getTypeIcon = (type: string) => {
    const typeConfig = resourceTypes.find(t => t.value === type);
    return typeConfig?.icon || Home;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            className="bg-dark-800 border border-dark-600 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-dark-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-primary-400 to-primary-500 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Add Emergency Resource</h2>
                  <p className="text-sm text-gray-400">Help your community by adding emergency resources</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Resource Type */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">Resource Type *</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {resourceTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleInputChange('type', type.value)}
                        className={`p-4 rounded-lg border text-left transition-all ${
                          formData.type === type.value
                            ? 'border-primary-400 bg-primary-400/10'
                            : 'border-dark-600 hover:border-dark-500 bg-dark-700/50'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon className={`w-5 h-5 ${formData.type === type.value ? 'text-primary-400' : 'text-gray-400'}`} />
                          <div>
                            <div className="font-medium text-white">{type.label}</div>
                            <div className="text-xs text-gray-400">{type.description}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Basic Information */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Resource Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`w-full px-4 py-3 bg-dark-700 border rounded-lg focus:outline-none transition-colors ${
                      errors.name ? 'border-accent-red' : 'border-dark-600 focus:border-primary-400'
                    }`}
                    placeholder="e.g., Downtown Emergency Shelter"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-accent-red flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Contact Information *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.contact}
                      onChange={(e) => handleInputChange('contact', e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 bg-dark-700 border rounded-lg focus:outline-none transition-colors ${
                        errors.contact ? 'border-accent-red' : 'border-dark-600 focus:border-primary-400'
                      }`}
                      placeholder="Phone number or email"
                    />
                  </div>
                  {errors.contact && (
                    <p className="mt-1 text-sm text-accent-red flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      {errors.contact}
                    </p>
                  )}
                </div>
              </div>

              {/* Location Information */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Location Name *</label>
                <input
                  type="text"
                  value={formData.location_name}
                  onChange={(e) => handleInputChange('location_name', e.target.value)}
                  className={`w-full px-4 py-3 bg-dark-700 border rounded-lg focus:outline-none transition-colors ${
                    errors.location_name ? 'border-accent-red' : 'border-dark-600 focus:border-primary-400'
                  }`}
                  placeholder="e.g., Community Center, Hospital, School"
                />
                {errors.location_name && (
                  <p className="mt-1 text-sm text-accent-red flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    {errors.location_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Street Address *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className={`flex-1 px-4 py-3 bg-dark-700 border rounded-lg focus:outline-none transition-colors ${
                      errors.address ? 'border-accent-red' : 'border-dark-600 focus:border-primary-400'
                    }`}
                    placeholder="123 Main Street"
                  />
                  <button
                    type="button"
                    onClick={handleGeocodeAddress}
                    disabled={isGeocoding}
                    className="px-4 py-3 bg-primary-400 hover:bg-primary-500 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                    title="Get coordinates from address"
                  >
                    {isGeocoding ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </button>
                </div>
                {errors.address && (
                  <p className="mt-1 text-sm text-accent-red flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    {errors.address}
                  </p>
                )}
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">City *</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className={`w-full px-4 py-3 bg-dark-700 border rounded-lg focus:outline-none transition-colors ${
                      errors.city ? 'border-accent-red' : 'border-dark-600 focus:border-primary-400'
                    }`}
                    placeholder="City"
                  />
                  {errors.city && (
                    <p className="mt-1 text-sm text-accent-red flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      {errors.city}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">State/Region</label>
                  <input
                    type="text"
                    value={formData.region}
                    onChange={(e) => handleInputChange('region', e.target.value)}
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="State or Region"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Country Code</label>
                  <input
                    type="text"
                    value={formData.country_code}
                    onChange={(e) => handleInputChange('country_code', e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="US"
                    maxLength={2}
                  />
                </div>
              </div>

              {/* Coordinates */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Latitude</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => handleInputChange('latitude', parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="0.000000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Longitude</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => handleInputChange('longitude', parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="0.000000"
                  />
                </div>
              </div>

              {/* Capacity and Status */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Total Capacity *</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      min="1"
                      value={formData.capacity}
                      onChange={(e) => handleInputChange('capacity', parseInt(e.target.value) || 0)}
                      className={`w-full pl-10 pr-4 py-3 bg-dark-700 border rounded-lg focus:outline-none transition-colors ${
                        errors.capacity ? 'border-accent-red' : 'border-dark-600 focus:border-primary-400'
                      }`}
                      placeholder="100"
                    />
                  </div>
                  {errors.capacity && (
                    <p className="mt-1 text-sm text-accent-red flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      {errors.capacity}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Available Spaces *</label>
                  <input
                    type="number"
                    min="0"
                    max={formData.capacity}
                    value={formData.available}
                    onChange={(e) => handleInputChange('available', parseInt(e.target.value) || 0)}
                    className={`w-full px-4 py-3 bg-dark-700 border rounded-lg focus:outline-none transition-colors ${
                      errors.available ? 'border-accent-red' : 'border-dark-600 focus:border-primary-400'
                    }`}
                    placeholder="50"
                  />
                  {errors.available && (
                    <p className="mt-1 text-sm text-accent-red flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      {errors.available}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                  >
                    {statusOptions.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className={`w-full px-4 py-3 bg-dark-700 border rounded-lg focus:outline-none transition-colors resize-none ${
                    errors.description ? 'border-accent-red' : 'border-dark-600 focus:border-primary-400'
                  }`}
                  rows={3}
                  placeholder="Describe the services, amenities, and any special requirements..."
                  maxLength={500}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-accent-red flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    {errors.description}
                  </p>
                )}
                <div className="flex justify-between mt-1 text-xs text-gray-400">
                  <span>Provide clear information about what this resource offers</span>
                  <span>{formData.description.length}/500</span>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-3 border border-dark-600 rounded-lg font-semibold text-gray-300 hover:bg-dark-700 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-3 bg-gradient-to-r from-primary-400 to-primary-500 rounded-lg font-semibold text-white hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Adding Resource...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Add Resource
                    </>
                  )}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddResourceModal;